/**
 * Staged inline-row flush helpers (fix/mo-flete-staged-row-capture).
 *
 * On both the create screen (BudgetFormNew) and the edit screen
 * (BudgetDetailNew), a Mano de Obra / Flete line is typed into an inline "add"
 * row that lives in staged component state (`newInstallation` / `newFreight`)
 * and is only pushed into `formData` when the user clicks the row-level add
 * button (`handleAddInstallation` / `handleAddFreight`). If the rep fills that
 * row but saves WITHOUT clicking the add button, the staged row used to be
 * silently dropped from the payload and the section came back empty on reopen.
 *
 * These pure helpers let the save path detect a filled-but-not-added staged row
 * and fold it into the effective installations/freights the payload is built
 * from, mirroring the exact validity checks + normalization the add-handlers
 * already use. They are deliberately UI-framework-free so they can be unit
 * tested and shared by both screens.
 */

export type InstallationMode = 'catalog' | 'custom';
export type FreightMode = 'catalog' | 'manual';

/** Minimal structural shape of a staged installation entry. */
export interface StagedInstallationLike {
  installation_service_id?: number;
  installation_service?: { precio?: number; unidad?: string } | undefined;
  cantidad_manual?: number;
  trabajo?: string;
  mano_obra_m2?: number;
  unidad?: string;
  tipo_instalacion?: unknown;
}

/** Shape returned by normalizeStagedInstallation - the raw staged fields plus
 * the resolved price/cantidad/subtotal/unidad/tipo_instalacion. Extra fields on
 * the input (descripcion_trabajo, id, the full installation_service object, ...)
 * are preserved at runtime via the spread even though they are not typed here. */
export interface NormalizedStagedInstallation extends StagedInstallationLike {
  mano_obra_m2: number;
  cantidad_manual: number;
  subtotal: number;
  unidad?: string;
  tipo_instalacion?: unknown;
}

/** Minimal structural shape of a staged freight entry. */
export interface StagedFreightLike {
  tipo_envio?: string;
  freight_rate_id?: number;
}

/**
 * True when the staged installation row holds real, savable data.
 * Mirrors the early-return guards in `handleAddInstallation` on both screens:
 * a positive cantidad, plus (catalog) a selected service, or (custom) a
 * non-empty trabajo AND a mano_obra_m2.
 */
export function stagedInstallationHasData(
  ni: StagedInstallationLike | null | undefined,
  mode: InstallationMode,
): boolean {
  if (!ni) return false;
  if ((ni.cantidad_manual ?? 0) <= 0) return false;
  if (mode === 'catalog') return !!ni.installation_service_id;
  return !!(ni.trabajo && ni.trabajo.trim() && ni.mano_obra_m2);
}

/**
 * Normalize a staged installation into the same shape `handleAddInstallation`
 * commits into `formData` (resolves price, cantidad, subtotal, unidad,
 * tipo_instalacion). The payload builders on both screens re-derive
 * price/subtotal from these raw fields, so the load-bearing outputs here are
 * `cantidad_manual`, `mano_obra_m2`, `tipo_instalacion` and (for custom-global)
 * `unidad`.
 */
export function normalizeStagedInstallation(
  ni: StagedInstallationLike,
  mode: InstallationMode,
): NormalizedStagedInstallation {
  let precio: number;
  if (mode === 'catalog') {
    precio = ni.mano_obra_m2 || ni.installation_service?.precio || 0;
  } else {
    precio = ni.mano_obra_m2 || 0;
  }
  const cantidad = ni.cantidad_manual || 1;
  const esGlobal = mode === 'custom' && ni.unidad?.toLowerCase() === 'global';
  const subtotal = esGlobal ? precio : precio * cantidad;
  return {
    ...ni,
    mano_obra_m2: precio,
    cantidad_manual: cantidad,
    subtotal,
    unidad: mode === 'custom' ? (ni.unidad || '') : (ni.installation_service?.unidad || undefined),
    tipo_instalacion: mode === 'custom' ? 'custom' : undefined,
  };
}

/**
 * Return the environments list with `stagedInstallation` appended to the target
 * ambiente, mirroring `handleAddInstallation`'s env resolution:
 *   - a valid normal index -> that ambiente;
 *   - otherwise (the general index, null, or an out-of-range index) -> the
 *     general ambiente, found by name or created via `makeGeneralEnv`.
 * Returns the original array unchanged when there is no staged installation.
 * Never mutates the input.
 */
export function computeEffectiveInstallationEnvironments<
  E extends { nombre: string; installations?: any[] },
>(
  environments: E[],
  stagedInstallation: any | null,
  targetIndex: number | null,
  opts: { generalEnvName: string; makeGeneralEnv: (inst: any) => E },
): E[] {
  if (!stagedInstallation) return environments;

  const envs = environments.map((e) => ({
    ...e,
    installations: [...(e.installations || [])],
  })) as E[];

  const useNormal =
    targetIndex != null && targetIndex >= 0 && !!envs[targetIndex];

  if (useNormal) {
    (envs[targetIndex as number] as any).installations = [
      ...((envs[targetIndex as number] as any).installations || []),
      stagedInstallation,
    ];
    return envs;
  }

  // General ambiente fallback (targetIndex is the general index, null, or oob).
  const gi = envs.findIndex((e) => e.nombre === opts.generalEnvName);
  if (gi === -1) {
    envs.push(opts.makeGeneralEnv(stagedInstallation));
  } else {
    (envs[gi] as any).installations = [
      ...((envs[gi] as any).installations || []),
      stagedInstallation,
    ];
  }
  return envs;
}

/**
 * True when the staged freight row holds real, savable data.
 * Mirrors `handleAddFreight`'s guards: (catalog) a selected rate, or (manual)
 * a non-empty tipo_envio.
 */
export function stagedFreightHasData(
  nf: StagedFreightLike | null | undefined,
  mode: FreightMode,
): boolean {
  if (!nf) return false;
  if (mode === 'catalog') return !!nf.freight_rate_id;
  return !!(nf.tipo_envio && nf.tipo_envio.trim());
}

/** Effective freights list: committed freights plus a valid staged one. */
export function computeEffectiveFreights<F>(
  freights: F[],
  stagedFreight: F | null,
): F[] {
  return stagedFreight ? [...freights, stagedFreight] : freights;
}
